/**
 * Store-facing copy for integration sync settings.
 * Keep IntegrationSettingsDrawer and IntegrationsPage in sync via this module.
 */

export const INTEGRATION_SETTINGS_COPY = {
  intro:
    'These options control what Motzu writes to your store on the next sync. Turning something off skips that data — it does not delete what is already in your store.',

  dataSync: {
    title: 'Data sync',
    subtitle:
      'Choose which Motzu fields are pushed when products sync. Only products you have selected for sync are affected.',
  },

  syncProducts: {
    label: 'Products',
    desc:
      'Updates title, description, vendor, product type, and other catalog details from Motzu. Store-specific overrides (if you set them) take priority over Motzu defaults.',
  },
  syncPricing: {
    label: 'Pricing',
    desc:
      'Writes Motzu price (and compare-at / MAP-derived compare price when available) onto variants. Turn off if you manage prices only inside Shopify or another system.',
  },
  syncImages: {
    label: 'Images',
    desc:
      'Uploads product photos from Motzu during sync. Existing store images may be replaced or supplemented depending on sync mode.',
  },
  includeMarketingImages: {
    label: 'Include marketing / lifestyle images',
    desc:
      'Off by default so promotional, lifestyle, and icon images stay out of the product gallery. Turn on only if you want those extras on the storefront.',
  },
  syncInventory: {
    label: 'Inventory',
    desc:
      'Sets a starting inventory quantity on synced products. Motzu is not a live inventory system — use this for an initial count, then manage stock in your store or ERP.',
  },
  defaultInventoryCount: {
    helper: 'Applied when a product is first synced (or when inventory sync runs). Per-product overrides win when set.',
    // Flag `initial_sync_mode`: the count is its own setting, never tied to the inventory sync switch.
    label: 'Starting inventory count',
    helperStandalone:
      'Set on a product when Motzu first creates it in your store. Always available, and separate from inventory sync, which keeps stock levels matched between two systems after that. Per-product overrides win when set.',
  },
  planLock: {
    syncInventory:
      'Inventory sync between Motzu and your store is not in your current plan, so it stays off. This never blocks setup, and the starting inventory count still applies to new products.',
    customMetafieldMapping:
      'Mapping MAP, prices and rebate fields onto your own metafields is on Intelligence and Complete. Identity, spec, launch date and compatibility fields map on every plan, and the fixed Motzu fields keep writing as they do today.',
  },
  groupKitBodyAsVariants: {
    label: 'Group kits and configurations as variants',
    desc:
      'On by default for Shopify. Body, kit, and color SKUs share one product with a Style/config switcher. Turn off to create a separate listing for each Motzu SKU.',
  },

  proOnly: {
    title: 'Pro-only products on public storefront',
    subtitle:
      'Some Motzu catalog items are marked Pro-only (dealer / program restricted). By default they are skipped so they never appear on a public web store. Enable only if your vendor agreements allow selling them on this channel.',
    toggleLabel: 'Allow Pro-only products in sync',
    toggleDesc:
      'When on, Pro-only items in your sync selection can be pushed like other products. You must confirm acceptance the first time; Motzu records who accepted and when.',
  },

  tagsAutomation: {
    title: 'Tags & automation',
    subtitle: 'How Motzu labels products in your store and when sync runs.',
  },
  addMotzuTag: {
    label: 'Add “motzu_synced” tag',
    desc:
      'Adds a motzu_synced tag on products Motzu creates or updates so you can filter them in Shopify Admin, bulk edits, and Motzu tools (for example duplicate cleanup).',
  },
  tagMode: {
    label: 'Tags Motzu adds',
    motzu: 'Motzu tags',
    markerOnly: 'Only motzu_synced',
    motzuDesc: 'Motzu adds its generated tags (brand, category, series, mount, sensor size) along with motzu_synced. Your own tags are never removed.',
    markerOnlyDesc:
      'Motzu adds only the motzu_synced tag and leaves every other tag to you. Pre-order, available-date and discontinued tags are still added, because sync depends on them. A tag override set on a single product still applies.',
  },
  autoSync: {
    label: 'Auto sync',
    desc:
      'Runs sync on a schedule for products already in your sync selection. Turn off to sync only when you click Sync manually.',
  },
  syncInterval: {
    helper:
      'How often Motzu queues an automatic sync while Auto sync is on. Daily means at least 24 hours after the last finished sync — the checker runs every 15 minutes, so the clock time can vary.',
  },
  syncTime: {
    label: 'Start at a set time',
    desc:
      'Start each automatic sync at the same local time, so it lands before or after your own jobs. Each scheduled time runs once; if the last sync is still running then, it starts as soon as that one finishes, up to an hour later, otherwise it waits for the next one.',
    timeLabel: 'Start time',
    minuteLabel: 'Minutes past the hour',
    weekdayLabel: 'Day',
    zonePrefix: "In your store's time zone,",
    zoneUnknown: "Read in your store's time zone (Store Settings).",
    nextPrefix: 'Next sync:',
    dueNow: "Next sync: the {time} run hasn't happened yet, so it starts within 15 minutes of saving.",
    intervalHelper:
      'How often Motzu runs an automatic sync while Auto sync is on. Turn on "Start at a set time" to pin the start time; otherwise a sync runs once the interval has passed since the last finished one, so the clock time can vary.',
  },
  autoSyncNewProducts: {
    label: 'Auto-sync new products',
    desc:
      'When Motzu ingests a new product for brands you sell, add it to your sync selection automatically. Existing store products are not removed.',
  },
  syncRefurbishedProducts: {
    label: 'Include refurbished products',
    desc:
      'Allow refurbished catalog items in sync and auto-add. Off keeps refurbished SKUs out of this store channel.',
  },
  syncRentalProducts: {
    label: 'Include rental / loaner kits',
    desc:
      'Allow rental and loaner kit products in sync and auto-add. Off keeps those SKUs out of this store channel.',
  },
  orderSync: {
    label: 'Order sync',
    desc:
      'Pulls order/sales data from Shopify so Motzu can build sell-through reports. Does not change products on your storefront.',
  },

  dataCollection: {
    title: 'What we collect',
    intro:
      'Connecting Shopify lets Motzu sync your catalog and, if you turn on order sync, pull order line items for sell-through and rebate reports.',
    collectLabel: 'We copy',
    collect:
      'SKU, barcode, product name, quantity, price, discounts, currency, order date, order number, and payment or fulfillment status.',
    notCollectLabel: 'We do not copy',
    notCollect: 'Customer name, email, phone, or address.',
    retention:
      'We keep this while the integration is connected and delete it when you disconnect, close the Motzu account, or ask us to.',
    privacyLink: 'Privacy Policy',
  },

  scopeLock: {
    notGranted:
      'Shopify did not approve this permission. Leave it off, or reconnect to grant it.',
    grant: 'Grant in Shopify',
    customApp:
      'Add this permission on your Shopify app, then check permissions on the integration card.',
  },

  oauthExtras: {
    title: 'Also ask Shopify for',
    intro:
      'Catalog access is always requested (products, listings, locations). These extras are on for most stores. Turn one off if Cin7 or another system already owns that data.',
    inventory: {
      label: 'Inventory',
      desc: 'Read and set stock when inventory sync is on. Leave off if your ERP owns quantity.',
    },
    orders: {
      label: 'Orders',
      desc: 'Read order line items for sell-through and rebate reports. Does not change the storefront.',
    },
    discounts: {
      label: 'Checkout discounts',
      desc: 'Apply vendor rebates as Shopify discounts. Leave off if you manage discounts elsewhere.',
    },
    afterInstallTitle: 'Product sync after install',
    afterInstall:
      'After you connect, open Integrations → Sync Behavior to choose which product data Motzu writes (products, pricing, images) and the sync mode (link only, enrich, or override). Those are not extra Shopify permissions.',
    laterTitle: 'Adding these later',
    laterMotzu:
      'Open Integrations → Sync Behavior. A locked toggle means Shopify has not approved that permission. Click Grant in Shopify, approve it, then turn the setting on. Motzu will not write inventory or pull orders until you do.',
    laterCustom:
      'Enable the matching Admin API scopes on your Shopify app (inventory, orders, or discounts), then click Check permissions on the integration card. Do not use Motzu Sync’s Grant in Shopify — that would replace your custom-app token.',
    customAppNote:
      'Your Shopify app must also have these extras enabled. Motzu can only request scopes that app allows. Match the switches above to the app’s Admin API scopes, or leave extras off here and add them on the app later.',
    tokenNote:
      'A pasted Admin API token already has the scopes set on that app. To add inventory, orders, or discounts later, enable them on the Shopify app, then Check permissions on the integration card.',
    markets: {
      label: 'Markets / price zones',
      desc: 'Read markets and set a per-zone price. Leave off if you do not use Shopify Markets pricing.',
    },
  },

  syncMode: {
    title: 'Sync mode',
    subtitle:
      'Controls how Motzu treats products that are already linked between Motzu and your store. New products Motzu creates always get full Motzu data; "Pricing and rebates only" never creates any.',
    // Lightspeed wording of `link_only`: a sync links the items Catalog match found and creates nothing.
    linkOnly: {
      label: 'Link only',
      desc:
        'Link the Lightspeed items Catalog match found to their Motzu products. Motzu never creates items and never changes names, descriptions or photos. Turn on the switch below to also keep prices up to date on linked items.',
    },
    linkOnlyPricing: {
      label: 'Keep prices and rebates up to date on linked items',
      desc:
        'After linking, each sync updates the price (with instant rebates applied) and compare-at price of items already linked. Items that are not linked are skipped, never created. Needs Pricing on under Data sync.',
      pricingOff: 'Pricing is off under Data sync, so prices will not be updated.',
    },
    // Shopify wording of the same `link_only` mode. Not a fourth mode: it is the
    // dealer-facing promise for the mode that matches products and prices them.
    // With flag `initial_sync_mode` on, the same card says what the mode does to products already in
    // the store and points at the separate "New products" choice.
    linkOnlyShopifyInitial: {
      label: 'Pricing and rebates only (Link only)',
      desc:
        'Updates products already in your store. Motzu matches them by SKU or barcode, then updates only price, compare-at price and rebate info. Titles, photos and descriptions stay yours. Products your store does not have yet follow "Products Motzu has not seen in your store yet" below.',
    },
    initialSync: {
      title: 'Products Motzu has not seen in your store yet',
      subtitle:
        'What happens to a product you selected that has no listing in your Shopify store. Products already in your store always follow the mode above.',
      create: {
        label: 'Create them',
        desc:
          'The first sync creates the product in Shopify with its title, description, photos, specs and price. After that, the mode above applies to it.',
      },
      skip: {
        label: 'Skip them',
        desc:
          'Motzu does not create new listings, in any mode. Products already in your store are still matched by SKU or barcode and follow the mode above. Your ERP or team creates products.',
      },
      skipNote: 'Skip means Motzu does not create new listings. It does not change your sync mode. Your ERP or team creates products.',
    },
    linkOnlyShopify: {
      label: 'Pricing and rebates only (Link only)',
      desc:
        'Match my existing Shopify products, never create new ones. Motzu matches by SKU or barcode, then updates only price, compare-at price and rebate info. Titles, photos and descriptions stay yours, and anything Motzu cannot match is skipped.',
    },
    enrich: {
      label: 'Fill gaps (Enrich)',
      desc:
        'Fill in missing Motzu data (for example metafields, images, or tags) without overwriting fields your store already has.',
    },
    override: {
      label: 'Full listing (Override)',
      desc:
        'Full Motzu sync — replace linked product fields with Motzu catalog data (respecting which Data sync toggles are on). Best when Motzu should own the listing.',
    },
    lightspeedSubtitle:
      'How Motzu treats items already in Lightspeed. An item Motzu creates gets the full catalog record. Link only records matches from Catalog match.',
    lightspeedLinkOnly: {
      label: 'Link only',
      desc:
        'Link the Lightspeed items Catalog match found and keep prices and rebates up to date. Names, notes, and photos stay as they are on the item. Turn the switch off to link and leave prices as they are.',
    },
    lightspeedEnrich: {
      label: 'Fill gaps (Enrich)',
      desc:
        'Fill empty item fields: name, SKU, barcode, manufacturer, category, and the private note. Photos are added when the item has none. The Motzu tag is added. Prices still update. Fields the item already has stay as they are.',
    },
    lightspeedOverride: {
      label: 'Full listing (Override)',
      desc:
        'Replace the Lightspeed fields you choose below with Motzu catalog data. Prices follow the Pricing switch. Photos are added when the item has none.',
    },
  },

  overrideFields: {
    title: 'What Override replaces',
    subtitle:
      'Applies to products already linked to your store. Unchecked fields keep whatever your store has today. Pricing, inventory and spec metafields follow the Data sync toggles above.',
    keepNote: 'Products Motzu creates always get full Motzu data.',
    fields: {
      title: {
        label: 'Title',
        desc: 'Replaced with the Motzu product title. The search-engine title is set from the catalog SEO title, or from this title.',
      },
      description: {
        label: 'Description',
        desc: 'Replaced with Motzu copy plus spec sections. Your own description is lost once replaced. The meta description is set from the catalog SEO description, or from the short description.',
      },
      images: {
        label: 'Images',
        desc: 'Replaced with up to 10 Motzu images (needs Images data sync on). Uncheck to keep your own photos.',
      },
      tags: { label: 'Tags', desc: 'Motzu tags are merged into your existing tags; nothing is removed.' },
      vendorStatus: { label: 'Vendor and status', desc: 'Brand name and active / draft / archived from the Motzu lifecycle.' },
      productType: { label: 'Product type and category', desc: 'Replaced with Motzu taxonomy and Shopify standard category.' },
      weight: {
        label: 'Weight and dimensions',
        desc: 'Shipping weight on the variant and the weight metafield, from the Motzu catalog. Uncheck to keep the weight and dimensions you set in Shopify (used for weight-based and carrier shipping rates).',
      },
    },
  },

  lightspeed: {
    inventory: {
      desc:
        'Sets a starting quantity on each shop when Motzu creates the item. Quantities already on an item stay as they are.',
      helper:
        'Used on each shop only when Motzu creates the item. Later syncs leave those quantities as they are. Removing a product from the selection leaves the Lightspeed item in place: archiving an item in Lightspeed sets its stock to zero.',
    },
    motzuTag: {
      desc:
        'Adds motzu_synced to the item’s tags when Motzu creates or updates it. The item’s other tags stay in place.',
    },
    overrideFields: {
      subtitle:
        'Applies to items already linked. A field you leave unchecked keeps what the item has today. Pricing follows the Pricing switch. The SKU stays so Motzu can match the item again.',
      keepNote: 'An item Motzu creates gets the full catalog record.',
      fields: {
        title: { label: 'Name', desc: 'The name on the receipt and the item label.' },
        description: {
          label: 'Private note',
          desc: 'The catalog description, stored as a private item note. It is kept off the receipt.',
        },
        images: {
          label: 'Photos',
          desc: 'Added when the item has no photos. Photos already on the item stay in place.',
        },
        tags: { label: 'Motzu tag', desc: 'Adds motzu_synced and keeps the item’s other tags.' },
        vendorStatus: { label: 'Manufacturer', desc: 'Sets the item manufacturer from the Motzu brand.' },
        productType: { label: 'Category', desc: 'Sets the Lightspeed category from the Motzu product type.' },
      },
    },
  },

  storefrontPresets: {
    title: 'Product page fields',
    subtitle:
      'Camera products need extra details Shopify does not include by default — specs, what’s in the box, key features. Set those fields up once so Motzu can fill them and your theme can show them.',
    howItWorksTitle: 'How this works',
    steps: [
      'Set up the field labels in Shopify. You will confirm before Motzu writes anything.',
      'Sync products. That is when Motzu fills in the actual specs and other details.',
      'Add Motzu blocks to your theme and click Save in Shopify. That is when shoppers see them.',
    ],
    fieldsTitle: 'What fields Motzu adds',
    fieldsIntro:
      'This first step adds the labels only — the empty extra boxes. It does not fill products yet. Specs are included.',
    fieldGroups: [
      {
        title: 'Specs and camera details',
        items:
          'Sensor size and type, megapixels, ISO, lens mount, focal length, aperture, zoom, stabilization, autofocus, display, viewfinder, shutter, burst rate, video, flash, exposure, connectivity, USB, memory cards, image formats, features, and weight.',
      },
      {
        title: 'Spec tables for the product page',
        items:
          'A ready-made specifications table (HTML), the same specs as structured data (JSON), and a one-line key-specs summary such as “24.2 MP | Full Frame | 4K”.',
      },
      {
        title: 'Other product-page content',
        items: 'Key features list, what’s in the box, manuals and downloads, videos, and pre-order notice and message.',
      },
      {
        title: 'Identifiers and pricing extras',
        items: 'MPN, UPC, EAN, active rebates, sale amount, regular price, and where the synced price came from.',
      },
    ],
    adminNote:
      'A few of these (sensor size, megapixels, mount, video class, rebates, spec table, in the box, key-specs line, pre-order message) can also show on the product in Shopify Admin after setup.',
    fieldSwitches: {
      title: 'Motzu fields on this integration',
      intro:
        'Turn a field off and Motzu leaves it out of every sync to this store, and does not create its label. A value already on a product stays; Motzu never deletes it. Everything is on by default.',
      columnLabel: 'Write',
      switchLabel: (name: string) => `Write ${name} to Shopify`,
      weightNote: 'Weight follows the “Weight” row under Override fields in Sync Behavior.',
      saved: 'Saved. The next sync follows it.',
      saveFailed: 'Could not save the field switches.',
      offCount: (n: number) => `${n} off`,
    },
    provisionLabel: 'Set up product spec fields',
    themeEditorLabel: 'Add blocks to your theme',
    cardNote:
      'Set up field labels first, then add theme blocks. Neither button changes your live website by itself — you confirm field setup here, and you save in Shopify to publish blocks.',
    guard: {
      title: 'Set up extra product fields in Shopify?',
      whatItIsTitle: 'What this is',
      whatItIs:
        'A Shopify product already has a title, price, and photos. Camera gear also needs extras like sensor size, lens mount, and what’s in the box. Shopify stores those extras as metafields — extra boxes on the product. Before Shopify will use them, it needs a list of which boxes exist. Shopify calls that list metafield definitions.',
      fieldsTitle: 'What fields we add',
      fieldsIntro:
        'Yes — specs are the main set. Motzu adds about 50 product field labels, including:',
      whatHappensTitle: 'What happens if you confirm',
      whatHappens:
        'Motzu adds those field labels to this store right away. You may see some of them when you open a product in Shopify Admin. Shoppers will not see anything new on your website from this step. Specs and other values land on a listing after Motzu syncs that product. They appear on the storefront only after you add Motzu blocks in the theme editor and click Save there.',
      writesNow:
        'Confirming starts immediately. There is no second prompt. This writes field labels to Shopify — not product titles, prices, photos, or the spec values themselves.',
      staysSameTitle: 'Existing stores and existing data',
      staysSame:
        'Safe on a brand-new store and on a store that already has products. Motzu does not overwrite titles, prices, photos, inventory, theme files, or metafield values already on listings. If a field label already exists and matches Motzu’s format, it is left as-is (Motzu may only make it visible to your theme). If you or another app already created the same field in a different format, Motzu skips that one — your data stays, and Motzu will not fill that field on later syncs. After you confirm, any skipped or failed fields are listed in the result.',
      acknowledge:
        'I understand Motzu will add these extra product field labels to this Shopify store now.',
      confirm: 'Add the fields',
      working: 'Adding fields…',
      retry: 'Try again',
      close: 'Close',
      doneTitle: 'Product fields are set up',
      doneWithIssuesTitle: 'Product fields set up, with some skipped',
      failedTitle: 'Could not set up the product fields',
      autoClose: 'This window closes on its own in a few seconds.',
      counts: {
        created: 'added',
        updated: 'updated',
        alreadyExists: 'already there',
      },
      conflictsTitle: 'Skipped — already exists in a different format',
      failedFieldsTitle: 'Failed',
    },
    themeEditor: {
      title: 'Show Motzu blocks on your product pages',
      intro:
        'These links open Shopify’s theme editor with a Motzu section ready to place — specifications, key features, what’s in the box, manuals, videos, or a pre-order notice. Your live site does not change until you click Save in Shopify.',
      requiresDefs:
        'Set up the product spec fields first. Until those field labels exist, the blocks have nothing to show.',
      requiresOs2:
        'This works with current Shopify themes. Very old (vintage) themes cannot add app blocks.',
      notConfiguredTitle: 'Theme blocks are not available yet',
      notConfigured:
        'Motzu is still finishing the Shopify side of this feature, so there is nothing for you to do here yet. Product fields and syncs work without it. Contact Motzu support if you need the blocks sooner.',
    },
  },

  presetGallery: {
    title: 'Storefront look',
    subtitle:
      'Pick a Motzu look, set your brand colours, preview the derived theme, then download a zip to upload in Shopify.',
    galleryEmpty: 'No storefront looks are available yet.',
    galleryLoadError: 'Could not load storefront looks.',
    screenshotPending: 'Screenshot coming soon — the description is the look.',
    exampleColoursCaption: 'Shown in example colours — yours are set next.',
    chooseLook: 'Customise this look',
    steps: {
      look: { label: '1 · Pick a look', value: 'Choose a starting look' },
      brand: { label: '2 · Brand colours', value: 'Your colours, previewed' },
      download: { label: '3 · Download', value: 'Theme zip for Shopify' },
      inProgress: 'In progress',
      changeLink: 'Change',
    },
    brandTitle: 'Brand colours',
    brandIntro:
      'One colour is enough. An optional second colour is used for sale and highlight sections. The preview uses the same colour engine as the download.',
    primaryLabel: 'Primary colour',
    accentLabel: 'Accent colour (optional)',
    logoLabel: 'Logo (optional, preview only)',
    logoHint: 'Shown on the mock storefront. The downloaded theme does not include this file yet.',
    previewTitle: 'Live preview',
    previewInYourColours: '{name} in your colours',
    previewCaption:
      'Header and hero use your brand colour; cards use a soft tint; the footer stays dark. Every text and button pair is checked for readability.',
    contrastChecked: '{n} colour pairs checked',
    contrastAdjusted: '{k} adjusted for readability',
    contrastAllPass: 'all pass as picked',
    accentDerivedPlaceholder: 'Derived: {hex}',
    clearAccentLabel: 'Clear',
    backToGallery: 'Back to looks',
    continueToDownload: 'Continue to download',
    deliveryTitle: 'Download and upload',
    deliveryIntro: 'Motzu builds a Dawn theme zip with this look and your colours. Shopify still needs the upload.',
    downloadLabel: 'Download theme',
    downloadingLabel: 'Building theme…',
    uploadTitle: 'Upload in Shopify',
    uploadSteps: [
      'Download the branded theme zip from Motzu.',
      'In Shopify Admin, go to Online Store → Themes → Add theme → Upload zip file.',
      'Preview the uploaded theme, then publish when it looks right.',
    ],
    appliedTitle: 'Applied look',
    appliedEmpty: 'No Motzu look has been downloaded for this store yet.',
    appliedMeta: 'Version {version} · Dawn {dawn}',
    reapplyLabel: 'Re-apply',
    changeColorsLabel: 'Change colours',
    appliedNote:
      'This is the last look Motzu stamped for you. Your live Shopify theme changes only after you upload the zip.',
    persistFailed:
      'Theme zip was built but the applied look could not be saved. Try again so it stays after reload.',
    appliedPrimary: 'Primary',
    appliedAccent: 'Accent',
  },
} as const;

/** The mode chip on an integration card: every mode has a label, Override included. */
export function syncModeChipLabel(platform: string, mode: 'link_only' | 'enrich' | 'override'): string {
  if (mode === 'link_only') return platform === 'shopify' ? 'Pricing & rebates only (Link only)' : 'Link Only';
  return mode === 'enrich' ? 'Fill gaps (Enrich)' : 'Full listing (Override)';
}
